import dayjs from 'dayjs';

/**
 * One derivation for every trial day-count the product renders — the billing
 * hero, the usage strip and the calendar top-bar pill — so they cannot state
 * different numbers for the same account.
 *
 * `daysLeft` is the API's number and stays the API's number. Since api #927
 * (`Kontentino\NewBilling\Support\RemainingDays::between`) both
 * `/desktopapp/billing/trial.daysLeft` and `/desktopapp/billing/subscription.
 * renewsInDays` are the same quantity computed by the same code:
 *
 *   whole days remaining until the period ends, rounded UP;
 *   0 only once the period has actually ended.
 *
 * So on day 1 of a 14-day trial the customer is told 14 days, not 13 — the
 * inclusive count they were sold. `dayOfTrial` is the API's 1-based elapsed
 * index and satisfies `dayOfTrial + daysLeft = totalDays + 1` by construction,
 * so deriving the eyebrow day back out of `daysLeft` reproduces exactly what
 * the API sent while making it impossible for the two to drift apart. That is
 * what B1 (register #26) was: not a wrong field, but two fields rendered side
 * by side that nothing kept in step.
 *
 * `isLastDay` is decided by the calendar date in `endsOn`, never by a day
 * count. `daysLeft === 1` only means "less than 24h remain", which on a trial
 * ending 20 Aug 09:08 becomes true on 19 Aug 09:08 — saying "today is the last
 * day" then would take a day off the customer's trial in the copy.
 */
export type TrialDays = {
  totalDays: number;
  dayOfTrial: number;
  daysLeft: number;
  progressPct: number;
  isLastDay: boolean;
};

export type TrialDaysSource = {
  dayOfTrial?: number | null;
  totalDays?: number | null;
  daysLeft?: number | null;
  endsOn?: string | null;
};

/** A wire value we can count with, or `null` when the payload is unusable. */
const wholeNumber = (value: number | null | undefined): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const floored = Math.floor(value);
  return floored >= 0 ? floored : null;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const deriveTrialDays = (trial: TrialDaysSource): TrialDays => {
  const wireTotal = wholeNumber(trial.totalDays);
  const wireDaysLeft = wholeNumber(trial.daysLeft);
  const wireDay = wholeNumber(trial.dayOfTrial);

  // Fallback for a payload missing `daysLeft`, using the API's own identity
  // (`TrialService`): dayOfTrial + daysLeft = totalDays + 1.
  const daysLeftFromDay =
    wireTotal !== null && wireDay !== null && wireDay > 0
      ? wireTotal - wireDay + 1
      : null;

  const rawDaysLeft = wireDaysLeft ?? daysLeftFromDay ?? 0;
  // A payload with no readable `totalDays` still gets a coherent (if
  // uninformative) progress bar rather than a NaN width.
  const totalDays = Math.max(1, wireTotal ?? rawDaysLeft);
  const daysLeft = clamp(rawDaysLeft, 0, totalDays);
  const dayOfTrial = clamp(totalDays - daysLeft + 1, 1, totalDays);

  const endsOn = trial.endsOn ? dayjs(trial.endsOn) : null;

  return {
    totalDays,
    dayOfTrial,
    daysLeft,
    progressPct: clamp(Math.round((dayOfTrial / totalDays) * 100), 0, 100),
    isLastDay:
      endsOn !== null && endsOn.isValid() && endsOn.isSame(dayjs(), 'day'),
  };
};
